import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { useState, useEffect, useCallback } from 'react';

// Mini Prince for navbar animation
function NavPrince({ state, position }) {
    const getTransform = () => {
        switch(state) {
            case 'running':
                return 'scaleX(1)';
            case 'knocked':
                return 'scaleX(1) rotate(90deg)';
            case 'getting-up':
                return 'scaleX(1) rotate(45deg)';
            default:
                return 'scaleX(1)';
        }
    };

    return (
        <div 
            className="absolute transition-all duration-300 ease-out"
            style={{ 
                left: `${position}%`,
                bottom: '4px',
                transform: getTransform(),
                transformOrigin: 'bottom center'
            }}
        >
            <svg viewBox="0 0 16 20" width="20" height="25" style={{ imageRendering: 'pixelated' }}>
                {/* Hair */}
                <rect x="5" y="0" width="6" height="3" fill="#1a1a1a"/>
                {/* Face */}
                <rect x="5" y="3" width="6" height="4" fill="#D4A574"/>
                {/* Eyes */}
                <rect x="6" y="4" width="1" height="1" fill="#1a1a1a"/>
                <rect x="9" y="4" width="1" height="1" fill="#1a1a1a"/>
                {/* Body */}
                <rect x="4" y="7" width="8" height="5" fill="#F5F5F5"/>
                <rect x="4" y="11" width="8" height="1" fill="#EAB308"/>
                {/* Legs */}
                <rect x="5" y="12" width="2" height="6" fill="#DC2626"/>
                <rect x="9" y="12" width="2" height="6" fill="#DC2626"/>
                {/* Feet */}
                <rect x="4" y="18" width="3" height="2" fill="#8B4513"/>
                <rect x="9" y="18" width="3" height="2" fill="#8B4513"/>
                {/* Sword */}
                <rect x="0" y="6" width="1" height="6" fill="#E5E7EB"/>
                <rect x="-1" y="11" width="3" height="1" fill="#EAB308"/>
            </svg>
        </div>
    );
}

// Mini Sultan for navbar animation
function NavSultan({ casting }) {
    return (
        <div 
            className="absolute right-[15%] bottom-1"
            style={{ transform: 'scaleX(-1)' }}
        >
            <svg viewBox="0 0 20 24" width="24" height="30" style={{ imageRendering: 'pixelated' }}>
                {/* Crown/Turban */}
                <rect x="6" y="0" width="8" height="3" fill="#7C3AED"/>
                <rect x="5" y="3" width="10" height="2" fill="#7C3AED"/>
                <rect x="9" y="0" width="2" height="2" fill="#EAB308"/>
                {/* Face */}
                <rect x="7" y="5" width="6" height="5" fill="#D4A574"/>
                {/* Evil red eyes */}
                <rect x="8" y="7" width="1" height="1" fill={casting ? "#FF0000" : "#DC2626"}/>
                <rect x="11" y="7" width="1" height="1" fill={casting ? "#FF0000" : "#DC2626"}/>
                {/* Beard */}
                <rect x="8" y="10" width="4" height="2" fill="#1F2937"/>
                {/* Body - dark robes */}
                <rect x="5" y="12" width="10" height="6" fill="#1F2937"/>
                <rect x="6" y="13" width="8" height="4" fill="#7C3AED"/>
                {/* Legs */}
                <rect x="7" y="18" width="2" height="4" fill="#1F2937"/>
                <rect x="11" y="18" width="2" height="4" fill="#1F2937"/>
                {/* Staff */}
                <rect x="16" y="6" width="2" height="14" fill="#EAB308"/>
                <rect x="15" y="4" width="4" height="3" fill={casting ? "#A855F7" : "#9333EA"}/>
                {/* Casting glow */}
                {casting && (
                    <>
                        <rect x="14" y="3" width="6" height="1" fill="#C084FC" opacity="0.7"/>
                        <rect x="14" y="8" width="6" height="1" fill="#C084FC" opacity="0.7"/>
                    </>
                )}
            </svg>
        </div>
    );
}

// Lightning bolt effect
function Thunderbolt({ active, targetX }) {
    if (!active) return null;
    
    return (
        <div 
            className="absolute thunderbolt-strike"
            style={{ 
                left: `${targetX}%`,
                top: '0',
                bottom: '0',
                width: '30px',
                zIndex: 10
            }}
        >
            <svg viewBox="0 0 20 50" width="30" height="100%" style={{ imageRendering: 'pixelated' }}>
                {/* Main bolt */}
                <polygon 
                    points="10,0 12,15 16,15 8,30 10,30 4,50 6,28 2,28 8,12 6,12" 
                    fill="#EAB308"
                    className="bolt-flash"
                />
                {/* Inner glow */}
                <polygon 
                    points="9,5 11,15 13,15 9,25 10,25 7,40 8,24 6,24 9,14 8,14" 
                    fill="#FFFFFF"
                    opacity="0.8"
                />
            </svg>
            {/* Impact sparks */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
                <div className="spark spark-1" />
                <div className="spark spark-2" />
                <div className="spark spark-3" />
            </div>
        </div>
    );
}

// Knocked down effect (stars)
function KnockedStars({ active }) {
    if (!active) return null;
    
    return (
        <div className="absolute knocked-stars" style={{ left: '15%', top: '2px' }}>
            <span className="star-spin">✦</span>
            <span className="star-spin delay-1">✧</span>
            <span className="star-spin delay-2">✦</span>
        </div>
    );
}

export function Navigation({ onBattleComplete }) {
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [battleState, setBattleState] = useState('idle'); // idle, running, casting, struck, knocked, getting-up
    const [princePosition, setPrincePosition] = useState(5);
    const [loopCount, setLoopCount] = useState(0);
    const [battleActive, setBattleActive] = useState(true);

    const isHome = location.pathname === '/';

    // Stop battle when user clicks a career box
    const stopBattle = useCallback(() => {
        setBattleActive(false);
        setBattleState('idle');
    }, []);

    // Expose stopBattle to parent
    useEffect(() => {
        if (onBattleComplete) {
            window.stopNavBattle = stopBattle;
        }
        return () => {
            delete window.stopNavBattle;
        };
    }, [onBattleComplete, stopBattle]);

    // Battle animation loop
    useEffect(() => {
        if (!isHome || !battleActive || loopCount >= 3) {
            setBattleState('idle');
            return;
        }

        let timeoutId;

        const runBattleSequence = async () => {
            // Phase 1: Prince starts running toward Sultan
            setBattleState('running');
            setPrincePosition(5);
            
            // Animate prince running
            for (let pos = 5; pos <= 55; pos += 5) {
                await new Promise(r => {
                    timeoutId = setTimeout(r, 80);
                });
                if (!battleActive) return;
                setPrincePosition(pos);
            }
            
            // Phase 2: Sultan casts thunderbolt
            setBattleState('casting');
            await new Promise(r => {
                timeoutId = setTimeout(r, 300);
            });
            if (!battleActive) return;
            
            // Phase 3: Prince gets struck
            setBattleState('struck');
            await new Promise(r => {
                timeoutId = setTimeout(r, 400);
            });
            if (!battleActive) return;
            
            // Phase 4: Prince knocked down
            setBattleState('knocked');
            await new Promise(r => {
                timeoutId = setTimeout(r, 800);
            });
            if (!battleActive) return;
            
            // Phase 5: Prince getting up
            setBattleState('getting-up');
            await new Promise(r => {
                timeoutId = setTimeout(r, 500);
            });
            if (!battleActive) return;
            
            // Phase 6: Prince brushes off, moves back
            setBattleState('idle');
            setPrincePosition(5);
            await new Promise(r => {
                timeoutId = setTimeout(r, 300);
            });
            if (!battleActive) return;
            
            // Increment loop count
            setLoopCount(prev => prev + 1);
        };

        runBattleSequence();

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isHome, battleActive, loopCount]);

    return (
        <nav className="sticky top-0 z-50 bg-background border-b-2 border-border" data-testid="main-navigation">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16 relative">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 z-20" data-testid="nav-logo">
                        <span className="text-xl font-bold text-primary" style={{ fontFamily: "'Silkscreen', cursive" }}>
                            SS
                        </span>
                        <span className="hidden sm:inline text-sm uppercase tracking-widest">
                            Shreyan
                        </span>
                    </Link>

                    {/* Battle Arena - only show on home page and desktop */}
                    {isHome && battleActive && loopCount < 3 && (
                        <div className="hidden md:block absolute left-[20%] right-[25%] h-full overflow-hidden">
                            {/* Floor line */}
                            <div className="absolute bottom-2 left-0 right-0 h-0.5 bg-border" />
                            
                            {/* Prince */}
                            <NavPrince 
                                state={battleState} 
                                position={princePosition}
                            />
                            
                            {/* Sultan */}
                            <NavSultan casting={battleState === 'casting' || battleState === 'struck'} />
                            
                            {/* Thunderbolt */}
                            <Thunderbolt 
                                active={battleState === 'struck'} 
                                targetX={princePosition + 5}
                            />
                            
                            {/* Knocked stars */}
                            <KnockedStars active={battleState === 'knocked'} />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-3 z-20">
                        {/* Theme Toggle */}
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={toggleTheme}
                            className="rounded-none border-2 retro-btn"
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? (
                                <Sun className="h-4 w-4" />
                            ) : (
                                <Moon className="h-4 w-4" />
                            )}
                        </Button>

                        {/* LinkedIn */}
                        <a
                            href="https://www.linkedin.com/in/shreyansahukar/"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="linkedin-link"
                        >
                            <Button
                                variant="default"
                                className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn hidden sm:flex items-center gap-2"
                            >
                                <Linkedin className="h-4 w-4" />
                                <span className="text-xs uppercase tracking-widest">LinkedIn</span>
                            </Button>
                            <Button
                                variant="default"
                                size="icon"
                                className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn sm:hidden"
                            >
                                <Linkedin className="h-4 w-4" />
                            </Button>
                        </a>
                    </div>
                </div>
            </div>
        </nav>
    );
}
